import { formatUsDateShort } from '../../../lib/dateHelpers';
import { APPOINTMENT_TITLE_SEPARATOR } from '../../appointments/utils/buildAppointmentDisplayTitle';
import type { Episode } from './types';

export interface EpisodeSelectOption {
  value: string;
  label: string;
}

/** Minimal episode shape pickers receive; provider/startDate enable disambiguation when present. */
export type EpisodePickerItem = Pick<Episode, 'id' | 'name'> &
  Partial<Pick<Episode, 'provider' | 'startDate'>> & {
    clientName?: string;
  };

/**
 * Display label for an Episode of Care in a picker.
 * Matches appointment/calendar titles: `{Client} — {Episode}` when clientName is set.
 * When several episodes share a name, provider and start date are appended.
 */
export function formatEpisodeOptionLabel(
  episode: EpisodePickerItem,
  siblings: ReadonlyArray<Pick<Episode, 'id' | 'name'>>,
): string {
  const name = episode.name || episode.id;
  const base = episode.clientName?.trim()
    ? `${episode.clientName.trim()}${APPOINTMENT_TITLE_SEPARATOR}${name}`
    : name;
  const ambiguous = siblings.some((s) => s.id !== episode.id && (s.name || s.id) === name);
  if (!ambiguous) return base;
  const parts = [base];
  if (episode.provider) parts.push(episode.provider);
  if (episode.startDate) parts.push(`started ${formatUsDateShort(episode.startDate)}`);
  return parts.join(' · ');
}

/** `Select` data for a list of episodes, with ambiguous names disambiguated. */
export function toEpisodeSelectOptions(episodes: ReadonlyArray<EpisodePickerItem>): EpisodeSelectOption[] {
  return episodes.map((e) => ({ value: e.id, label: formatEpisodeOptionLabel(e, episodes) }));
}
